<template>
  <div
    class="horizontal-menu-scroll-wrapper"
    :class="{ 'has-overflow-start': showStart, 'has-overflow-end': showEnd }"
  >
    <div
      ref="scrollContainer"
      class="navbar-container main-menu-content scroll-menu"
      @scroll="updateFades"
    >
      <horizontal-nav-menu-items :items="navMenuItems" />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, nextTick } from '@vue/composition-api'
import navMenuItems from '@/navigation/horizontal'
import HorizontalNavMenuItems from './components/horizontal-nav-menu-items/HorizontalNavMenuItems.vue'
import { getUserData } from '@/libs/utils/user'
import internal_campaigns from '@/services/internal_campaigns'

const INTERNAL_CAMPAIGNS_ITEM = {
  title: 'invitationCampaigns',
  img_svg: 'campaign_int_black',
  route: 'creator-campaigns',
  resource: 'Creator',
  action: 'manage',
}

const TALENTOS_ITEM = {
  title: 'talentos',
  img_svg: 'proposals_black',
  route: 'talentos',
  resource: 'Creator',
  action: 'manage',
  newLabel: false,
  disabled: false,
}

export default {
  components: {
    HorizontalNavMenuItems,
  },
  setup() {
    const scrollContainer = ref(null)
    const showStart = ref(false)
    const showEnd = ref(false)
    const menuItems = ref([...navMenuItems])

    const updateFades = () => {
      const el = scrollContainer.value
      if (!el) return
      const { scrollLeft, scrollWidth, clientWidth } = el
      showStart.value = scrollLeft > 1
      showEnd.value = scrollLeft + clientWidth < scrollWidth - 1
    }

    const checkInternalCampaigns = async () => {
      const user = getUserData()
      const isCreator = user && user.main_group && user.main_group.name === 'creator'
      if (!isCreator) return

      const alreadyAdded = menuItems.value.some(i => i.route === 'creator-campaigns')
      if (alreadyAdded) return

      const data = await internal_campaigns.hasInternalCampaigns()
      if (data && data.has_campaign_influencer_user_network) {
        menuItems.value = [...menuItems.value, INTERNAL_CAMPAIGNS_ITEM]
        nextTick(updateFades)
      }
    }

    const swapProposalsForTalentos = () => {
      const user = getUserData()
      const isCreator = user && user.main_group && user.main_group.name === 'creator'
      if (!isCreator) return
      const hasAgency = (user.user_permissions || []).some(p => p.codename === 'user_agency')
      if (!hasAgency) return
      menuItems.value = menuItems.value.map(item => { return item.route === 'view_proposals_edit' ? TALENTOS_ITEM : item })
    }

    onMounted(() => {
      nextTick(updateFades)
      window.addEventListener('resize', updateFades)
      swapProposalsForTalentos()
      checkInternalCampaigns()
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateFades)
    })

    return {
      navMenuItems: menuItems,
      scrollContainer,
      showStart,
      showEnd,
      updateFades,
    }
  },
}
</script>

<style lang="scss">
@import "~@core/scss/base/core/menu/menu-types/horizontal-menu.scss";

.horizontal-menu-scroll-wrapper {
  position: relative;

  // Degradados (fade) en los bordes para indicar que hay más menús ocultos
  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: 40px;
    pointer-events: none; // no bloquea clicks en los menús
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: 2;
  }

  &::before {
    left: 0;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0)
    );
  }

  &::after {
    right: 0;
    background: linear-gradient(
      to left,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0)
    );
  }

  &.has-overflow-start::before {
    opacity: 1;
  }

  &.has-overflow-end::after {
    opacity: 1;
  }
}

// Soporte para modo oscuro: el fade usa el color de fondo del menú
.dark-layout .horizontal-menu-scroll-wrapper {
  &::before {
    background: linear-gradient(
      to right,
      rgba(40, 48, 70, 1),
      rgba(40, 48, 70, 0)
    );
  }
  &::after {
    background: linear-gradient(
      to left,
      rgba(40, 48, 70, 1),
      rgba(40, 48, 70, 0)
    );
  }
}
</style>
